import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AcademicCapIcon,
  AdjustmentsHorizontalIcon,
  BellIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  CheckCircleIcon,
  CheckIcon,
  ClipboardDocumentCheckIcon,
  CurrencyDollarIcon,
  InformationCircleIcon,
  MegaphoneIcon,
  ShieldCheckIcon,
  TrashIcon,
  UserGroupIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { BellIcon as BellSolidIcon } from '@heroicons/react/24/solid';
import { useNotifications } from '../../hooks/useNotifications';
import type { Notification } from '../../types';

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenCenter?: () => void;
}

type ReadFilter = 'all' | 'unread' | 'read';

const typeLabels: Record<Notification['type'], string> = {
  assignment: 'الواجبات',
  submission: 'التسليمات',
  grade: 'الدرجات',
  session: 'الجلسات',
  attendance: 'الحضور',
  payment: 'المدفوعات',
  relation: 'العلاقات',
  hr: 'الموارد البشرية',
  security: 'الأمن',
  announcement: 'الإعلانات',
};

const typeOptions = Object.entries(typeLabels) as Array<
  [Notification['type'], string]
>;

const getNotificationIcon = (
  type: Notification['type'],
  priority: Notification['priority']
) => {
  const baseClasses = 'h-5 w-5';
  const priorityColor =
    priority === 'high'
      ? 'text-red-500'
      : priority === 'medium'
        ? 'text-yellow-500'
        : 'text-blue-500';

  switch (type) {
    case 'assignment':
      return <AcademicCapIcon className={`${baseClasses} text-blue-500`} />;
    case 'submission':
      return (
        <ClipboardDocumentCheckIcon
          className={`${baseClasses} text-indigo-500`}
        />
      );
    case 'grade':
      return <ChartBarIcon className={`${baseClasses} text-purple-500`} />;
    case 'session':
      return <CalendarDaysIcon className={`${baseClasses} text-green-500`} />;
    case 'attendance':
      return <CheckCircleIcon className={`${baseClasses} text-emerald-500`} />;
    case 'payment':
      return <CurrencyDollarIcon className={`${baseClasses} text-yellow-500`} />;
    case 'relation':
      return <UserGroupIcon className={`${baseClasses} text-cyan-500`} />;
    case 'hr':
      return <UserGroupIcon className={`${baseClasses} text-violet-500`} />;
    case 'security':
      return <ShieldCheckIcon className={`${baseClasses} text-red-500`} />;
    case 'announcement':
      return <MegaphoneIcon className={`${baseClasses} text-primary-600`} />;
    default:
      return (
        <InformationCircleIcon
          className={`${baseClasses} ${priorityColor}`}
        />
      );
  }
};

const getPriorityColor = (priority: Notification['priority']) => {
  switch (priority) {
    case 'high':
      return 'border-r-red-500';
    case 'medium':
      return 'border-r-yellow-500';
    case 'low':
      return 'border-r-green-500';
    default:
      return 'border-r-gray-300';
  }
};

const formatDate = (value: Notification['createdAt']) => {
  const date = value instanceof Date ? value : new Date(value as unknown as string);
  if (Number.isNaN(date.getTime())) return 'تاريخ غير صحيح';

  const diffInMs = Date.now() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / 60000);
  const diffInHours = Math.floor(diffInMs / 3600000);
  const diffInDays = Math.floor(diffInMs / 86400000);

  if (diffInMinutes < 1) return 'الآن';
  if (diffInMinutes < 60) return `منذ ${diffInMinutes} دقيقة`;
  if (diffInHours < 24) return `منذ ${diffInHours} ساعة`;
  if (diffInDays < 7) return `منذ ${diffInDays} يوم`;

  return date.toLocaleDateString('ar-EG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const isSafeInternalPath = (path: string) =>
  path.startsWith('/') && !path.startsWith('//');

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  isOpen,
  onClose,
  onOpenCenter,
}) => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();
  const [filter, setFilter] = useState<ReadFilter>('all');
  const [typeFilter, setTypeFilter] = useState<Notification['type'] | 'all'>(
    'all'
  );

  const filteredNotifications = useMemo(
    () =>
      notifications.filter(notification => {
        if (filter === 'unread' && notification.read) return false;
        if (filter === 'read' && !notification.read) return false;
        if (typeFilter !== 'all' && notification.type !== typeFilter) {
          return false;
        }
        return true;
      }),
    [filter, notifications, typeFilter]
  );

  const openNotificationsPage = () => {
    onClose();
    if (onOpenCenter) {
      onOpenCenter();
      return;
    }
    navigate('/notifications');
  };

  const openAction = async (notification: Notification) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    if (notification.actionUrl && isSafeInternalPath(notification.actionUrl)) {
      onClose();
      navigate(notification.actionUrl);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className='fixed inset-0 z-50'
      role='dialog'
      aria-modal='true'
      aria-labelledby='notifications-panel-title'
    >
      <button
        type='button'
        className='fixed inset-0 cursor-default bg-transparent'
        onClick={onClose}
        aria-label='إغلاق لوحة الإشعارات'
      />

      <aside className='fixed left-3 right-3 top-20 flex max-h-[calc(100vh-6rem)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl ring-1 ring-black/5 transition-transform duration-300 ease-out dark:border-gray-700 dark:bg-gray-800 sm:left-6 sm:right-auto sm:w-[24rem]'>
        <div className='flex items-center justify-between border-b border-gray-200 bg-gradient-to-l from-primary-50 to-white p-4 dark:border-gray-700 dark:from-gray-800 dark:to-gray-800'>
          <div className='flex items-center'>
            <BellSolidIcon className='ml-2 h-5 w-5 text-primary-600' />
            <h2
              id='notifications-panel-title'
              className='text-base font-semibold text-gray-900 dark:text-white'
            >
              الإشعارات
            </h2>
            {unreadCount > 0 && (
              <span className='mr-2 rounded-full bg-red-500 px-2 py-0.5 text-xs font-medium text-white'>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>

          <div className='flex items-center gap-1'>
            {unreadCount > 0 && (
              <button
                type='button'
                onClick={markAllAsRead}
                className='rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-primary-400'
                title='تعليم الكل كمقروء'
                aria-label='تعليم كل الإشعارات كمقروءة'
              >
                <CheckIcon className='h-4 w-4' />
              </button>
            )}
            <button
              type='button'
              onClick={onClose}
              className='rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200'
              title='إغلاق'
              aria-label='إغلاق الإشعارات'
            >
              <XMarkIcon className='h-4 w-4' />
            </button>
          </div>
        </div>

        <div className='border-b border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40'>
          <div className='mb-2 flex items-center justify-between'>
            <div className='flex gap-1'>
              {(['all', 'unread', 'read'] as const).map(filterType => (
                <button
                  type='button'
                  key={filterType}
                  onClick={() => setFilter(filterType)}
                  className={`rounded-full px-2.5 py-1 text-xs transition-all ${
                    filter === filterType
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {filterType === 'all' && 'الكل'}
                  {filterType === 'unread' && 'غير مقروء'}
                  {filterType === 'read' && 'مقروء'}
                </button>
              ))}
            </div>
          </div>

          <select
            value={typeFilter}
            onChange={event =>
              setTypeFilter(event.target.value as Notification['type'] | 'all')
            }
            className='w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
            title='فلترة حسب النوع'
          >
            <option value='all'>جميع الأنواع</option>
            {typeOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className='min-h-0 flex-1 overflow-y-auto'>
          {loading ? (
            <div className='flex flex-col items-center justify-center py-12'>
              <div className='h-8 w-8 animate-spin rounded-full border-2 border-primary-600 border-t-transparent' />
              <span className='mt-3 text-sm text-gray-600 dark:text-gray-400'>
                جاري التحميل...
              </span>
            </div>
          ) : error ? (
            <div className='p-6 text-center'>
              <XCircleIcon className='mx-auto mb-2 h-10 w-10 text-red-500' />
              <p className='text-sm text-red-600 dark:text-red-400'>{error}</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className='p-8 text-center'>
              <BellIcon className='mx-auto mb-3 h-12 w-12 text-gray-300 dark:text-gray-600' />
              <p className='text-sm text-gray-500 dark:text-gray-400'>
                {filter === 'unread'
                  ? 'لا توجد إشعارات غير مقروءة'
                  : 'لا توجد إشعارات'}
              </p>
            </div>
          ) : (
            <div className='divide-y divide-gray-100 dark:divide-gray-700'>
              {filteredNotifications.map(notification => (
                <article
                  key={notification.id}
                  className={`border-r-2 p-3 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                    !notification.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                  } ${getPriorityColor(notification.priority)}`}
                >
                  <button
                    type='button'
                    className='flex w-full items-start gap-2.5 text-start'
                    onClick={() => openAction(notification)}
                  >
                    <span className='mt-0.5 shrink-0'>
                      {getNotificationIcon(
                        notification.type,
                        notification.priority
                      )}
                    </span>

                    <span className='min-w-0 flex-1'>
                      <span className='mb-1 flex items-start justify-between gap-2'>
                        <span
                          className={`text-xs font-medium leading-tight ${
                            !notification.read
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {notification.title}
                        </span>
                        {!notification.read && (
                          <span className='mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-600' />
                        )}
                      </span>

                      <span className='mb-2 block text-xs leading-relaxed text-gray-600 dark:text-gray-400'>
                        {notification.body}
                      </span>

                      <span className='flex items-center justify-between'>
                        <span className='text-[10px] text-gray-500 dark:text-gray-500'>
                          {formatDate(notification.createdAt)}
                        </span>

                        {notification.actionUrl &&
                          isSafeInternalPath(notification.actionUrl) && (
                            <span className='text-[10px] font-medium text-primary-600 dark:text-primary-400'>
                              عرض التفاصيل
                            </span>
                          )}
                      </span>
                    </span>
                  </button>

                  <div className='mt-2 flex justify-end'>
                    <button
                      type='button'
                      onClick={() => deleteNotification(notification.id)}
                      className='rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400'
                      title='حذف الإشعار'
                      aria-label='حذف الإشعار'
                    >
                      <TrashIcon className='h-3.5 w-3.5' />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        <div className='border-t border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800'>
          <button
            type='button'
            onClick={openNotificationsPage}
            className='flex w-full items-center justify-center rounded-lg px-3 py-2 text-xs text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
          >
            <AdjustmentsHorizontalIcon className='ml-1.5 h-3.5 w-3.5' />
            فتح مركز الإشعارات
          </button>
        </div>
      </aside>
    </div>
  );
};

export default NotificationsPanel;
